import { styled, keyframes } from '@mui/material/styles';

// ── Animations ──────────────────────────────────────────────

export const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const shimmer = keyframes`
  0%, 100% { transform: translateX(-100%) translateY(-100%); }
  50%      { transform: translateX(100%) translateY(100%); }
`;

export const bgPulse = keyframes`
  0%   { opacity: 1; transform: scale(1); }
  100% { opacity: 0.7; transform: scale(1.05); }
`;

export const float = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  25%      { transform: translate(30px, -30px) rotate(5deg); }
  50%      { transform: translate(-20px, 20px) rotate(-3deg); }
  75%      { transform: translate(15px, 10px) rotate(2deg); }
`;

export const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25%      { transform: translateX(-6px); }
  75%      { transform: translateX(6px); }
`;

export const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ── Layout ──────────────────────────────────────────────────

const LOGIN_FONT = "'Plus Jakarta Sans', sans-serif";

export const StyledRoot = styled('main')({
  fontFamily: LOGIN_FONT,
  background: '#161C24',
  color: '#FFFFFF',
  minHeight: '100vh',
  overflow: 'hidden',
  position: 'relative',
});

export const BgOverlay = styled('div')({
  position: 'fixed',
  inset: 0,
  zIndex: 0,
  pointerEvents: 'none',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: [
      'radial-gradient(ellipse 80% 60% at 20% 80%, rgba(0, 171, 85, 0.10) 0%, transparent 60%)',
      'radial-gradient(ellipse 60% 50% at 80% 20%, rgba(0, 123, 85, 0.07) 0%, transparent 50%)',
    ].join(','),
    animation: `${bgPulse} 8s ease-in-out infinite alternate`,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(ellipse 80% 80% at 50% 50%, transparent 40%, rgba(22, 28, 36, 0.4) 100%)',
  },
});

// ── Floating Orbs ───────────────────────────────────────────

const orbBase = {
  position: 'fixed' as const,
  borderRadius: '50%',
  filter: 'blur(80px)',
  zIndex: 0,
};

export const Orb1 = styled('div')({
  ...orbBase,
  width: 500,
  height: 500,
  background: '#00AB55',
  top: -150,
  left: -150,
  opacity: 0.25,
  animation: `${float} 20s ease-in-out infinite`,
});

export const Orb2 = styled('div')({
  ...orbBase,
  width: 400,
  height: 400,
  background: '#007B55',
  bottom: -100,
  right: -100,
  opacity: 0.2,
  animation: `${float} 25s ease-in-out infinite -7s`,
});

export const Orb3 = styled('div')({
  ...orbBase,
  width: 300,
  height: 300,
  background: '#5BE584',
  top: '40%',
  right: '5%',
  opacity: 0.15,
  animation: `${float} 18s ease-in-out infinite -13s`,
});

// ── Scene ───────────────────────────────────────────────────

export const Scene = styled('div')({
  position: 'relative',
  zIndex: 1,
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
});

export const LoginContainer = styled('div')({
  width: '100%',
  maxWidth: 440,
  animation: `${slideUp} 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
  opacity: 0,
  transform: 'translateY(30px)',
});

// ── Brand ───────────────────────────────────────────────────

export const Brand = styled('div')({
  textAlign: 'center',
  marginBottom: 40,
  animation: `${slideUp} 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.1s forwards`,
  opacity: 0,
  transform: 'translateY(30px)',
});

export const BrandLogo = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 72,
  height: 72,
  borderRadius: 20,
  background: 'linear-gradient(135deg, #00AB55 0%, #007B55 100%)',
  marginBottom: 20,
  boxShadow: '0 8px 24px rgba(0, 171, 85, 0.25), 0 0 0 1px rgba(0, 171, 85, 0.1)',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(135deg, transparent 40%, rgba(255,255,255,0.1) 50%, transparent 60%)',
    animation: `${shimmer} 3s ease-in-out infinite`,
  },
  '& svg': {
    width: 36,
    height: 36,
    fill: '#FFFFFF',
    position: 'relative',
    zIndex: 1,
  },
  '@media (max-width: 480px)': {
    width: 60,
    height: 60,
    borderRadius: 16,
    '& svg': { width: 28, height: 28 },
  },
});

export const BrandTitle = styled('div')({
  fontSize: 28,
  fontWeight: 800,
  letterSpacing: -0.5,
  marginBottom: 6,
  background: 'linear-gradient(135deg, #FFFFFF 0%, #C4CDD5 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  '@media (max-width: 480px)': {
    fontSize: 24,
  },
});

export const BrandSubtitle = styled('div')({
  fontSize: 14,
  color: '#919EAB',
  fontWeight: 400,
  letterSpacing: 0.3,
});

// ── Card ────────────────────────────────────────────────────

export const StyledCard = styled('div')({
  background: 'rgba(33, 43, 54, 0.55)',
  backdropFilter: 'blur(24px) saturate(1.2)',
  WebkitBackdropFilter: 'blur(24px) saturate(1.2)',
  border: '1px solid rgba(145, 158, 171, 0.08)',
  borderRadius: 20,
  padding: '40px 36px',
  boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.02) inset',
  animation: `${slideUp} 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards`,
  opacity: 0,
  transform: 'translateY(30px)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '@media (max-width: 480px)': {
    padding: '32px 24px',
    borderRadius: 16,
  },
});

export const CardHeader = styled('div')({
  marginBottom: 32,
  '& h2': {
    fontFamily: LOGIN_FONT,
    fontSize: 22,
    fontWeight: 700,
    color: '#FFFFFF',
    margin: 0,
    marginBottom: 6,
  },
  '& p': {
    fontFamily: LOGIN_FONT,
    fontSize: 14,
    color: '#919EAB',
    margin: 0,
  },
});

// ── Input Fields ────────────────────────────────────────────

export const Field = styled('div')({
  position: 'relative',
  marginBottom: 24,
});

export const FieldInput = styled('input')({
  width: '100%',
  padding: '16px 48px 16px 18px',
  background: 'rgba(145, 158, 171, 0.06)',
  border: '1.5px solid rgba(145, 158, 171, 0.15)',
  borderRadius: 12,
  color: '#FFFFFF',
  fontFamily: LOGIN_FONT,
  fontSize: 15,
  fontWeight: 500,
  transition: 'all 0.25s ease',
  outline: 'none',
  '&::placeholder': {
    color: 'transparent',
  },
  '&:hover': {
    borderColor: 'rgba(145, 158, 171, 0.3)',
    background: 'rgba(145, 158, 171, 0.08)',
  },
  '&:focus': {
    borderColor: '#00AB55',
    background: 'rgba(0, 171, 85, 0.04)',
    boxShadow: '0 0 0 3px rgba(0, 171, 85, 0.32)',
  },
  '&:focus + label, &:not(:placeholder-shown) + label': {
    top: -8,
    transform: 'translateY(0)',
    fontSize: 12,
    color: '#00AB55',
    fontWeight: 600,
    background: '#212B36',
    padding: '0 6px',
    borderRadius: 4,
  },
});

export const FieldLabel = styled('label')({
  position: 'absolute',
  left: 18,
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: 15,
  color: '#919EAB',
  pointerEvents: 'none',
  transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
  fontWeight: 500,
  fontFamily: LOGIN_FONT,
});

export const FieldIcon = styled('span')({
  position: 'absolute',
  right: 16,
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#637381',
  cursor: 'pointer',
  transition: 'color 0.2s',
  display: 'flex',
  alignItems: 'center',
  '&:hover': {
    color: '#C4CDD5',
  },
  '& svg': {
    width: 20,
    height: 20,
  },
});

// ── Login Button ────────────────────────────────────────────

export const LoginButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'loading',
})<{ loading?: boolean }>(({ loading }) => ({
  width: '100%',
  padding: 16,
  marginTop: 8,
  background: 'linear-gradient(135deg, #00AB55 0%, #007B55 100%)',
  border: 'none',
  borderRadius: 12,
  color: '#FFFFFF',
  fontFamily: LOGIN_FONT,
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: 0.5,
  cursor: loading ? 'default' : 'pointer',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 16px rgba(0, 171, 85, 0.3)',
  pointerEvents: loading ? 'none' : 'auto',
  opacity: loading ? 0.85 : 1,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)',
    opacity: 0,
    transition: 'opacity 0.3s',
  },
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 8px 24px rgba(0, 171, 85, 0.4)',
    '&::before': { opacity: 1 },
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: '0 2px 8px rgba(0, 171, 85, 0.3)',
  },
}));

export const BtnText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'loading',
})<{ loading?: boolean }>(({ loading }) => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  opacity: loading ? 0 : 1,
}));

export const BtnArrow = styled('svg')({
  transition: 'transform 0.3s ease',
  'button:hover &': {
    transform: 'translateX(4px)',
  },
});

export const Spinner = styled('span')({
  position: 'absolute',
  width: 22,
  height: 22,
  border: '2.5px solid rgba(255,255,255,0.3)',
  borderTopColor: 'white',
  borderRadius: '50%',
  animation: `${spin} 0.8s linear infinite`,
  top: '50%',
  left: '50%',
  marginTop: -11,
  marginLeft: -11,
});

// ── Error Alert ─────────────────────────────────────────────

export const ErrorAlert = styled('div')({
  display: 'flex',
  padding: '14px 16px',
  background: 'rgba(255, 86, 48, 0.08)',
  border: '1px solid rgba(255, 86, 48, 0.16)',
  borderRadius: 10,
  marginBottom: 24,
  fontSize: 13,
  fontFamily: LOGIN_FONT,
  color: '#FF8A65',
  alignItems: 'center',
  gap: 10,
  animation: `${shake} 0.4s ease`,
  '& svg': {
    flexShrink: 0,
    width: 18,
    height: 18,
    fill: '#FF5630',
  },
});
